import {
  CommonWhen,
  CommonWhenTimespan,
  CommonWhenDate,
  CommonWhenDatespan,
  CommonWhenType,
  CommonWhenTime,
} from '../../../../../generated/graphql';
import { DateTime } from 'luxon';

// Type guards
export const isTimespanWhen = (
  when: CommonWhen,
): when is CommonWhenTimespan => {
  return when.type === CommonWhenType.Timespan;
};

export const isDateWhen = (when: CommonWhen): when is CommonWhenDate => {
  return when.type === CommonWhenType.Date;
};

export const isDatespanWhen = (
  when: CommonWhen,
): when is CommonWhenDatespan => {
  return when.type === CommonWhenType.Datespan;
};

export const isTimeWhen = (when: CommonWhen): when is CommonWhenTime => {
  return when.type === CommonWhenType.Time;
};

export const whenToCalendarEvent = (when: CommonWhen, baseEventProps: any) => {
  if (isTimespanWhen(when)) {
    return {
      ...baseEventProps,
      start: when.startTime,
      end: when.endTime,
      allDay: false,
      extendedProps: {
        ...baseEventProps.extendedProps,
        originalEvent: {
          ...baseEventProps.extendedProps?.originalEvent,
          when: {
            ...when,
            startTimezone: when.startTimezone || 'UTC',
            endTimezone: when.endTimezone || 'UTC',
          },
        },
      },
    };
  }

  if (isDateWhen(when)) {
    return {
      ...baseEventProps,
      start: when.date,
      end: when.date,
      allDay: true,
    };
  }

  if (isDatespanWhen(when)) {
    return {
      ...baseEventProps,
      start: when.startDate,
      end: when.endDate,
      allDay: true,
    };
  }

  if (isTimeWhen(when)) {
    return {
      ...baseEventProps,
      start: when.time,
      end: when.time,
      allDay: false,
      extendedProps: {
        ...baseEventProps.extendedProps,
        originalEvent: {
          ...baseEventProps.extendedProps?.originalEvent,
          when: {
            ...when,
            timezone: when.timezone || 'UTC',
          },
        },
      },
    };
  }

  return null;
};

export const calendarEventToWhen = (
  event: any,
  timezone: string,
): CommonWhen => {
  const isAllDay = event.allDay;

  if (isAllDay) {
    const startDate = event.start.toISOString().split('T')[0];
    const endDate = event.end?.toISOString().split('T')[0];

    if (endDate && startDate !== endDate) {
      return {
        type: CommonWhenType.Datespan,
        startDate,
        endDate,
      };
    } else {
      return {
        type: CommonWhenType.Date,
        date: startDate,
      };
    }
  }

  // Get the timezones from the event's extended props if available
  const startTimezone =
    event.extendedProps?.originalEvent?.when?.startTimezone || timezone;
  const endTimezone =
    event.extendedProps?.originalEvent?.when?.endTimezone || timezone;

  // Convert the event times to their respective timezones and then to ISO8601
  const startTime = DateTime.fromJSDate(event.start)
    .setZone(startTimezone)
    .toISO();
  const endTime = DateTime.fromJSDate(event.end).setZone(endTimezone).toISO();

  return {
    type: CommonWhenType.Timespan,
    startTime,
    endTime,
    startTimezone,
    endTimezone,
  };
};

export const formatWhen = (when: CommonWhen): string => {
  if (isDateWhen(when)) {
    return DateTime.fromISO(when.date).toLocaleString(DateTime.DATE_FULL);
  }

  if (isDatespanWhen(when)) {
    return `${DateTime.fromISO(when.startDate).toLocaleString(
      DateTime.DATE_FULL,
    )} - ${DateTime.fromISO(when.endDate).toLocaleString(DateTime.DATE_FULL)}`;
  }

  if (isTimespanWhen(when)) {
    const start = DateTime.fromISO(when.startTime).setZone(when.startTimezone);
    const end = DateTime.fromISO(when.endTime).setZone(when.endTimezone);

    return `${start.toLocaleString(
      DateTime.DATETIME_MED,
    )} - ${end.toLocaleString(DateTime.DATETIME_MED)}`;
  }

  if (isTimeWhen(when)) {
    return DateTime.fromISO(when.time)
      .setZone(when.timezone)
      .toLocaleString(DateTime.TIME_SIMPLE);
  }

  return '';
};
